/**
 * React hooks for the forms breakdown endpoint (page → forms).
 *
 * The funnel suggestions endpoint lists form targets without the page they live on, so the
 * funnel builder joins this page-first breakdown to give each form its page context.
 */

import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { useAuth } from "@/lib/contexts/AuthContext";
import { getFormsBreakdown, type FormsBreakdownResponse } from "@/lib/api/external-api";

type FormsBreakdownOptions = {
  start?: string; // ISO 8601
  end?: string; // ISO 8601
  limit?: number; // Pages returned (each page carries its top forms)
};

const DEFAULT_LIMIT = 100;

/**
 * The forms endpoints take a "granularity period" string rather than a range. Callers here
 * carry an ISO range (same one funnel suggestions use), so express it as the custom-range
 * form the API parses: `YYYY-MM-DD_YYYY-MM-DD`.
 */
const toGranularityPeriod = (start?: string, end?: string): string => {
  if (!start || !end) return "day_PAST_30_D";
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return "day_PAST_30_D";
  }
  return `${format(startDate, "yyyy-MM-dd")}_${format(endDate, "yyyy-MM-dd")}`;
};

/**
 * Forms grouped by page for a single property.
 */
export const useFormsBreakdown = (
  propertyId: string | undefined,
  options?: FormsBreakdownOptions
) => {
  const { token } = useAuth();
  const period = toGranularityPeriod(options?.start, options?.end);
  const limit = options?.limit ?? DEFAULT_LIMIT;

  return useQuery({
    queryKey: ["forms-breakdown", propertyId, period, limit],
    queryFn: async () => {
      if (!token || !propertyId) {
        throw new Error("Missing API token or property ID");
      }
      return await getFormsBreakdown(period, propertyId, token, limit);
    },
    enabled: Boolean(token && propertyId),
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });
};

/**
 * Same data for several properties in parallel (cross-domain funnel steps).
 * Returns a Map of propertyId -> FormsBreakdownResponse.
 */
export const useMultiPropertyFormsBreakdown = (
  propertyIds: string[],
  options?: FormsBreakdownOptions
): Map<string, FormsBreakdownResponse> => {
  const { token } = useAuth();
  const period = toGranularityPeriod(options?.start, options?.end);
  const limit = options?.limit ?? DEFAULT_LIMIT;

  const results = useQueries({
    queries: propertyIds.map((propertyId) => ({
      queryKey: ["forms-breakdown", propertyId, period, limit],
      queryFn: async () => {
        if (!token) throw new Error("Missing token");
        return await getFormsBreakdown(period, propertyId, token, limit);
      },
      enabled: Boolean(token && propertyId),
      staleTime: 10 * 60 * 1000,
      retry: 1,
    })),
  });

  return useMemo(() => {
    const map = new Map<string, FormsBreakdownResponse>();
    propertyIds.forEach((id, index) => {
      const data = results[index]?.data;
      if (data) map.set(id, data);
    });
    return map;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, propertyIds.join(",")]);
};
